import { supabase } from '../../../core/lib/supabase';
import { User, Session } from '@supabase/supabase-js';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
}

const AUTH_MS_LOG_PREFIX = "[AUTH_MS]";

function createAuthAttemptId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `auth-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function tokenPreview(token: string | undefined): string | null {
  if (!token) return null;
  if (token.length <= 12) return `${token.slice(0, 2)}...${token.slice(-2)}`;
  return `${token.slice(0, 6)}...${token.slice(-6)}`;
}

function getOfficeDiagnostics(): Record<string, unknown> {
  try {
    const diagnostics = Office.context?.diagnostics;
    return {
      host: diagnostics?.host,
      platform: diagnostics?.platform,
      version: diagnostics?.version,
    };
  } catch {
    return {};
  }
}

function authMsLog(level: "log" | "warn" | "error", payload: Record<string, unknown>): void {
  console[level](AUTH_MS_LOG_PREFIX, {
    ts: new Date().toISOString(),
    ...payload,
  });
}

class AuthService {
  private listeners: Array<(state: AuthState) => void> = [];

  constructor() {
    // Suscribirse a cambios de autenticación
    supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Auth state changed:', event, session?.user?.email);
      this.notifyListeners({
        user: session?.user || null,
        session: session || null,
        loading: false,
        error: null
      });
    });
  }

  /**
   * Suscribirse a cambios de autenticación
   */
  public subscribe(callback: (state: AuthState) => void): () => void {
    this.listeners.push(callback);
    
    // Devolver función para desuscribirse
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  private notifyListeners(state: AuthState): void {
    this.listeners.forEach(listener => listener(state));
  }

  /**
   * Login con email y password
   */
  public async signIn(email: string, password: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      console.log('🔐 Attempting login for:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('❌ Login error:', error.message);
        return {
          success: false,
          error: error.message
        };
      }

      if (!data.user) {
        return {
          success: false,
          error: 'No user returned from login'
        };
      }

      console.log('✅ Login successful:', data.user.email);
      console.log('👤 User ID:', data.user.id);

      return {
        success: true
      };
    } catch (error) {
      console.error('💥 Unexpected error during login:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Login con Microsoft usando Office Dialog API + Supabase OAuth.
   * El intercambio final de sesión se hace en el taskpane para evitar
   * problemas de almacenamiento entre webviews.
   */
  public async signInWithMicrosoft(): Promise<{
    success: boolean;
    userId?: string;
    error?: string;
  }> {
    return new Promise((resolve) => {
      const attemptId = createAuthAttemptId();
      const flowStartedAt = Date.now();

      try {
        const dialogUrl = `${window.location.origin}/auth-start.html`;

        authMsLog("log", {
          attemptId,
          stage: "dialog_open",
          status: "start",
          url: {
            origin: window.location.origin,
            path: "/auth-start.html",
          },
          office: getOfficeDiagnostics(),
        });

        Office.context.ui.displayDialogAsync(
          dialogUrl,
          { height: 60, width: 40, displayInIframe: false },
          (result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              const officeError = result.error as { code?: number; message?: string } | undefined;
              const dialogError =
                "message" in (result.error || {})
                  ? (result.error as { message: string }).message
                  : "No se pudo abrir la ventana de autenticación";
              authMsLog("error", {
                attemptId,
                stage: "dialog_open",
                status: "error",
                elapsedMs: Date.now() - flowStartedAt,
                errorCategory: "office_dialog_open_failed",
                officeErrorCode: officeError?.code,
                errorMessage: dialogError,
                office: getOfficeDiagnostics(),
              });
              resolve({
                success: false,
                error: dialogError,
              });
              return;
            }

            const dialog = result.value;
            let isSettled = false;
            authMsLog("log", {
              attemptId,
              stage: "dialog_open",
              status: "ok",
              elapsedMs: Date.now() - flowStartedAt,
              office: getOfficeDiagnostics(),
            });

            const finish = (payload: { success: boolean; userId?: string; error?: string }) => {
              if (isSettled) return;
              isSettled = true;
              authMsLog(payload.success ? "log" : "error", {
                attemptId,
                stage: "finish",
                status: payload.success ? "ok" : "error",
                elapsedMs: Date.now() - flowStartedAt,
                userId: payload.userId,
                errorMessage: payload.error,
              });
              try {
                dialog.close();
              } catch {
                // noop
              }
              resolve(payload);
            };

            dialog.addEventHandler(Office.EventType.DialogEventReceived, (args) => {
              const dialogArgs = args as { error?: number; type?: string } | undefined;
              authMsLog("warn", {
                attemptId,
                stage: "dialog_event",
                status: "error",
                elapsedMs: Date.now() - flowStartedAt,
                errorCategory: "office_dialog_event_received",
                officeErrorCode: dialogArgs?.error,
                eventType: dialogArgs?.type,
              });
              finish({
                success: false,
                error: `Dialog event received (code: ${dialogArgs?.error ?? "unknown"})`,
              });
            });

            dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args) => {
              try {
                const rawMessage = "message" in args ? args.message : "{}";
                authMsLog("log", {
                  attemptId,
                  stage: "dialog_message_received",
                  status: "start",
                  elapsedMs: Date.now() - flowStartedAt,
                  rawMessageLength: rawMessage?.length || 0,
                  rawMessagePreview: (rawMessage || "").slice(0, 240),
                });
                const parsed = JSON.parse(rawMessage || "{}");
                authMsLog("log", {
                  attemptId,
                  stage: "dialog_message_received",
                  status: "ok",
                  elapsedMs: Date.now() - flowStartedAt,
                  messageType: parsed?.type,
                  sourceAttemptId: parsed?.attemptId,
                  hasError: !!parsed?.error,
                });

                if (parsed?.type === "auth_error") {
                  authMsLog("error", {
                    attemptId,
                    stage: "dialog_message_auth_error",
                    status: "error",
                    elapsedMs: Date.now() - flowStartedAt,
                    errorCategory: "oauth_provider_error",
                    errorMessage: parsed.error || "Error durante autenticación con Microsoft",
                  });
                  finish({
                    success: false,
                    error: parsed.error || "Error durante autenticación con Microsoft",
                  });
                  return;
                }

                if (parsed?.type !== "auth_tokens") {
                  authMsLog("error", {
                    attemptId,
                    stage: "dialog_message_invalid",
                    status: "error",
                    elapsedMs: Date.now() - flowStartedAt,
                    errorCategory: "dialog_message_invalid",
                    messageType: parsed?.type,
                  });
                  finish({
                    success: false,
                    error: "Respuesta de autenticación no válida",
                  });
                  return;
                }

                const accessToken = parsed.accessToken as string | undefined;
                const refreshToken = parsed.refreshToken as string | undefined;

                if (!accessToken || !refreshToken) {
                  authMsLog("error", {
                    attemptId,
                    stage: "dialog_message_tokens_missing",
                    status: "error",
                    elapsedMs: Date.now() - flowStartedAt,
                    errorCategory: "oauth_no_tokens_returned",
                    hasAccessToken: !!accessToken,
                    hasRefreshToken: !!refreshToken,
                  });
                  finish({
                    success: false,
                    error: "No se recibieron tokens de sesión válidos",
                  });
                  return;
                }

                authMsLog("log", {
                  attemptId,
                  stage: "set_session",
                  status: "start",
                  elapsedMs: Date.now() - flowStartedAt,
                  hasAccessToken: true,
                  hasRefreshToken: true,
                  accessTokenLength: accessToken.length,
                  refreshTokenLength: refreshToken.length,
                  accessTokenPreview: tokenPreview(accessToken),
                  refreshTokenPreview: tokenPreview(refreshToken),
                });
                const { data, error } = await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken,
                });

                if (error) {
                  authMsLog("error", {
                    attemptId,
                    stage: "set_session",
                    status: "error",
                    elapsedMs: Date.now() - flowStartedAt,
                    errorCategory: "supabase_set_session_failed",
                    errorMessage: error.message || "No se pudo establecer la sesión",
                    errorName: (error as { name?: string }).name,
                  });
                  finish({
                    success: false,
                    error: error.message || "No se pudo establecer la sesión",
                  });
                  return;
                }

                authMsLog("log", {
                  attemptId,
                  stage: "set_session",
                  status: "ok",
                  elapsedMs: Date.now() - flowStartedAt,
                  userId: data.user?.id,
                  userEmail: data.user?.email,
                });
                finish({
                  success: true,
                  userId: data.user?.id,
                });
              } catch (err) {
                authMsLog("error", {
                  attemptId,
                  stage: "dialog_message_parse_or_process",
                  status: "error",
                  elapsedMs: Date.now() - flowStartedAt,
                  errorCategory: "dialog_message_processing_failed",
                  errorMessage: err instanceof Error ? err.message : "Error procesando respuesta de autenticación",
                });
                finish({
                  success: false,
                  error: err instanceof Error ? err.message : "Error procesando respuesta de autenticación",
                });
              }
            });
          }
        );
      } catch (error) {
        authMsLog("error", {
          attemptId,
          stage: "dialog_open",
          status: "error",
          elapsedMs: Date.now() - flowStartedAt,
          errorCategory: "unexpected_exception",
          errorMessage: error instanceof Error ? error.message : "Error iniciando login con Microsoft",
        });
        resolve({
          success: false,
          error: error instanceof Error ? error.message : "Error iniciando login con Microsoft",
        });
      }
    });
  }

  /**
   * Logout
   */
  public async signOut(): Promise<void> {
    try {
      console.log('🔐 Logging out...');
      
      // Verificar si hay una sesión antes de intentar logout
      const session = await this.getSession();
      
      if (!session) {
        console.log('ℹ️ No hay sesión activa, limpiando estado local...');
        // Si no hay sesión, simplemente notificar a los listeners que el usuario está deslogueado
        this.notifyListeners({
          user: null,
          session: null,
          loading: false,
          error: null
        });
        console.log('✅ Estado local limpiado');
        return;
      }
      
      // Si hay sesión, intentar hacer logout en Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        // Si el error es porque la sesión ya no existe, considerarlo como logout exitoso
        if (error.message?.includes('Auth session missing') || error.name === 'AuthSessionMissingError') {
          console.log('ℹ️ Sesión ya no existe, limpiando estado local...');
          this.notifyListeners({
            user: null,
            session: null,
            loading: false,
            error: null
          });
          console.log('✅ Logout completado (sesión ya había expirado)');
          return;
        }
        
        console.error('❌ Logout error:', error);
        throw error;
      }
      
      console.log('✅ Logged out successfully');
    } catch (error) {
      // Si ocurre un error pero es porque la sesión ya no existe, considerarlo como logout exitoso
      if (error instanceof Error && 
          (error.message?.includes('Auth session missing') || error.name === 'AuthSessionMissingError')) {
        console.log('ℹ️ Sesión ya no existe, limpiando estado local...');
        this.notifyListeners({
          user: null,
          session: null,
          loading: false,
          error: null
        });
        console.log('✅ Logout completado (sesión ya había expirado)');
        return;
      }
      
      console.error('💥 Error during logout:', error);
      throw error;
    }
  }

  /**
   * Obtener la sesión actual
   */
  public async getSession(): Promise<Session | null> {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('❌ Error getting session:', error);
        return null;
      }
      
      return data.session;
    } catch (error) {
      console.error('💥 Error getting session:', error);
      return null;
    }
  }

  /**
   * Obtener el usuario actual
   */
  public async getCurrentUser(): Promise<User | null> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('❌ Error getting user:', error);
        return null;
      }
      
      return user;
    } catch (error) {
      console.error('💥 Error getting user:', error);
      return null;
    }
  }

  /**
   * Verificar si hay una sesión activa
   */
  public async isAuthenticated(): Promise<boolean> {
    const session = await this.getSession();
    return session !== null;
  }

  /**
   * Obtener el user ID del usuario autenticado
   */
  public async getUserId(): Promise<string | null> {
    const user = await this.getCurrentUser();
    return user?.id || null;
  }
}

// Singleton instance
export const authService = new AuthService();



